import { action, extendObservable, makeObservable } from 'mobx';
import { CategoriaMinuta } from './Categoria';
import { Comentario } from './Comentario';
import ProcessoAssinaturaDto from './dto/ProcessoAssinaturaDto';
import { MovimentoGEDTO } from './MinutaMovimentos';
import { PeticaoApreciada } from './PeticaoApreciada';
import { Processo } from './Processo';
import { ISituacaoMinuta, MINUTA_COM_VISTO_ASSINADO, MINUTA_EM_ELABORACAO, SituacaoMinuta } from './SituacaoMinuta';
import { ITipoMinuta, TipoMinuta } from './TipoMinuta';

export interface IStatusRevisaoPautaMinuta {
    numero: number;
    codigoMagistrado: string;
    descricao: string;
}

export class Minuta {

    static foiEditadaNaFaseAnterior(minuta: Minuta): boolean {
        // O backend consulta o histórico de retificação de autuação do PJe (via pje-sessao) para
        // os casos em que minuta.codFase diverge da classe atual do processo, e só marca
        // editadaEmClasseAnterior quando a classe realmente mudou depois da última edição da
        // minuta - ver MinutaTemporariaDto.editadaEmClasseAnterior no backend.
        return minuta.editadaEmClasseAnterior === true;
    }

    static mobxInicial = () => ({
        idProcesso: 0,
        selecionada: false,
        mostrar: true,
        votoJaliberado: false,
        emEdicaoExterna: false,
        edicaoAguia: false,
        categorias: [],
        movimentosCNJ: [],
        minutaAssociada: new Minuta(),
        minutaPeticoes: [],
        semEmentaOuDecisao: '',
        publicavel: false,
        contemTrechoPubliquese: null,
        contemTrechoAposPauta: null,
        situacaoMinuta: new MINUTA_EM_ELABORACAO(),
        comentarios: [],
        vistoLiberado: false,
        dataVistoLiberado: undefined,
        minutaExterna: false,
        minutaParaAssinarDto: undefined,
        cpfRevisorPauta: undefined,
        textoRevisaoPauta: '',
        autorizacaoPauta: false,
        statusRevisao: undefined,
        textoFuncaoMagistrado: '',
        siglaOrgaoJulgador: ''
    });

    static criar(partial: Partial<Minuta>, processoPai?: Processo): Minuta {
        const minuta = new Minuta();
        extendObservable(minuta, Minuta.mobxInicial());
        const minutaMerge = Object.assign(minuta, partial);

        if (
            minutaMerge.isTipo(TipoMinuta.VOTO) &&
            minuta.situacaoMinuta.codigo === SituacaoMinuta.LIBERADA_PARA_ASSINATURA_OU_INCLUSAO_EM_PAUTA &&
            minutaMerge.vistoLiberado
        ) {
            minutaMerge.situacaoMinuta.descricao = new MINUTA_COM_VISTO_ASSINADO().descricao;
        }

        if (
            minuta.situacaoMinuta.codigo === SituacaoMinuta.LIBERADA_PARA_ASSINATURA_OU_INCLUSAO_EM_PAUTA &&
            !minutaMerge.vistoLiberado
        ) {
            minutaMerge.situacaoMinuta.descricao =
                'LIBERADA PARA ASSINATURA OU LIBERADA PARA INCLUSÃO EM PAUTA';
        }

        minutaMerge.idProcesso = processoPai ?
            processoPai.identificadorProcesso
            :
            (minutaMerge as any).identificadorProcesso;

        // --- Regras de arquivamento separadas ---
        const tipo = minutaMerge.tipo?.codigo;
        // const situacao = minutaMerge.situacaoMinuta?.codigo;

        if (tipo === TipoMinuta.VOTO &&
            minutaMerge.situacaoMinuta?.descricao === new MINUTA_COM_VISTO_ASSINADO().descricao) {
            minutaMerge.situacaoArquivamento = {
                podeArquivar: false,
                motivo: 'Minuta de voto com visto assinado não pode ser arquivada.'
            };
        } else {
            minutaMerge.situacaoArquivamento = {
                podeArquivar: true,
                motivo: ''
            };
        }

        if (minuta.tipo?.codigo === TipoMinuta.VOTO) {
            minutaMerge.semEmentaOuDecisao = minutaMerge.pendenciasEnvio || '';
        }
        // Processar movimentos
        minutaMerge.movimentosCNJ?.forEach(movimento => {
            const mov = MovimentoGEDTO.novo(movimento, processoPai);
            return mov.isComplementosPreenchidos;
        });

        return minutaMerge;
    }


    constructor() {
        this.situacaoMinuta = new MINUTA_EM_ELABORACAO();
        makeObservable(this, {
            selecionar: action,
            setMostrar: action,
            setEmEdicaoExterna: action
        });
    }

    idProcesso: number;
    tipo: ITipoMinuta;
    vistoLiberado: boolean;
    dataVistoLiberado?: Date;
    dataUltimaAlteracao: Date;
    // data: Date; //TODO Verificar se é necessário
    // processoControleGabinete: IProcessoControleGabinete;
    situacaoMinuta: ISituacaoMinuta;
    tipoDocumentoMinuta: string = 'docx';
    conteudo: string;
    siglaOrgaoJulgador: string;
    motivoNaoAssinavel: string;
    minutaParaAssinarDto: ProcessoAssinaturaDto;
    numero: number;
    cpfResponsavel: string;
    categorias: CategoriaMinuta[] = [];
    conteudoModelo: string; //deveria ser um base64
    codigoModelo: any;
    movimentosCNJ: MovimentoGEDTO[];
    isNova: boolean;
    selecionada: boolean;
    votoJaliberado: boolean;
    codUnAdmPub: number;
    identificadorGedAssinatura: string;
    emEdicaoExterna: boolean;
    edicaoAguia: boolean;
    publicavel: boolean;
    textoObservacao: string;
    textoFuncaoMagistrado: string;
    comentarios: Comentario[];
    contemTrechoPubliquese: boolean;
    contemTrechoAposPauta: boolean;

    semEmentaOuDecisao: string; // votos
    pendenciasEnvio: string; // despachos

    nomeResponsavel?: string; //TODO Eliminar atributo mutavel
    cpfUltimaAlteracao?: string; //TODO eliminar atributo mutavel
    mostrar?: boolean;
    codFase: string;
    editadaEmClasseAnterior?: boolean;
    minutaPeticoes: PeticaoApreciada[];
    minutaExterna?: boolean;
    cpfRevisorPauta?: string;
    textoRevisaoPauta?: string;
    autorizacaoPauta?: boolean;
    statusRevisao?: IStatusRevisaoPautaMinuta;

    situacaoArquivamento?: {
        podeArquivar: boolean;
        motivo: string;
    };

    selecionar(valor: boolean) {
        if (this.selecionada !== valor) {
            this.selecionada = valor;
        }
    }

    setMostrar(valor: boolean) {
        if (this.mostrar !== valor) {
            this.mostrar = valor;
        }
    }

    setEmEdicaoExterna(valor: boolean) {
        this.emEdicaoExterna = valor;
    }

    isTipo(tipo: number): boolean {
        return this.tipo && this.tipo.codigo === tipo;
    }

    isTipoDocumentoMinutaDocx(): boolean {
        return this.tipoDocumentoMinuta === 'docx';
    }

    isSituacao(situacao: ISituacaoMinuta) {
        if (situacao.codigo != 30) {
            return this.situacaoMinuta.codigo === situacao.codigo;
        }
        return this.situacaoMinuta.descricao === situacao.descricao;
    }

    get assinavel(): boolean {
        return this.minutaParaAssinarDto?.assinavel;
    }
}
